import { useEffect, useState } from "react";
import { Printer } from "lucide-react";
import QRCode from "qrcode";
import { useParams } from "react-router";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

import { voltAreas } from "../navigation/voltAreas";
import {
  attendanceErrorText,
  useRoomCode,
  useSessionDetail,
  useVoltRoles,
} from "./attendanceApi";
import { checkInUrl, formatLongDate, formatTime } from "./attendanceLogic";

const useQrImage = (text: string | null) => {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!text) return;
    let alive = true;
    QRCode.toString(text, { type: "svg", errorCorrectionLevel: "M", margin: 1 })
      .then((svg) => {
        if (alive)
          setSrc(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`);
      })
      .catch(() => alive && setSrc(null));
    return () => {
      alive = false;
    };
  }, [text]);
  return src;
};

const Message = ({ children }: { children: string }) => (
  <main className="mx-auto max-w-md p-8 text-center">
    <p role="alert">{children}</p>
  </main>
);

/** Print sheet for the door: QR to the check-in page, the code large and readable. */
export const AttendanceQrPage = () => {
  const { id } = useParams();
  const sessionId = Number(id);
  const { roles, ready } = useVoltRoles();
  const allowed = !!roles && voltAreas(roles).attendance;
  const detail = useSessionDetail(allowed ? sessionId : 0);
  const code = useRoomCode(allowed ? sessionId : 0);
  const url = code.data ? checkInUrl(code.data) : null;
  const qr = useQrImage(url);

  if (!ready) return <Skeleton className="m-8 h-96" />;
  if (!roles) return <Message>Bitte zuerst anmelden.</Message>;
  if (!allowed)
    return (
      <Message>
        Den QR-Code drucken nur Admins der Fraktion und Superadmins.
      </Message>
    );
  if (detail.isPending || code.isPending)
    return <Skeleton className="m-8 h-96" />;
  if (code.error) return <Message>{attendanceErrorText(code.error)}</Message>;
  const session = detail.data?.session;
  if (!session || !code.data) return <Message>Sitzung nicht gefunden.</Message>;
  if (session.cancelled)
    return <Message>Die Sitzung ist abgesagt, es gibt keinen QR-Code.</Message>;

  return (
    <main className="mx-auto flex min-h-svh max-w-xl flex-col items-center justify-center gap-6 bg-white p-8 text-center text-black">
      <header>
        <h1 className="text-3xl font-bold">{session.title}</h1>
        <p className="mt-2 text-lg">
          {formatLongDate(session.starts_at)}, {formatTime(session.starts_at)}–
          {formatTime(session.ends_at)} Uhr
          {session.location ? ` · ${session.location}` : ""}
        </p>
      </header>
      {qr ? (
        <img
          src={qr}
          alt={`QR-Code zum Einchecken: ${url}`}
          className="size-80 [image-rendering:pixelated]"
        />
      ) : (
        <Skeleton className="size-80" />
      )}
      <p className="text-xl">
        Handy-Kamera drauf, „Ich bin da“ tippen. Fertig.
      </p>
      <div>
        <p className="text-sm">Raumcode</p>
        <p
          className="font-mono text-6xl font-bold tracking-[0.2em]"
          aria-label={`Code ${code.data.split("").join(" ")}`}
        >
          {code.data}
        </p>
        <p className="mt-2 text-sm">
          Ohne Kamera: <span className="font-mono break-all">{url}</span>
        </p>
      </div>
      <Button onClick={() => window.print()} className="print:hidden">
        <Printer aria-hidden />
        Drucken
      </Button>
    </main>
  );
};

AttendanceQrPage.path = "/attendance/sessions/:id/qr";
